import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { getUserById, updateUser } from "../../../core/data/redux/user/userAction";
import ChangePasswordModal from './passwordUpdate';
import { Button, Modal } from "antd";
import Webcam from "react-webcam"; // Import react-webcam
import { all_routes } from "../../router/all_routes";

// Define types for the user information and other state properties
interface UserInfo {
  DisplayName: string;
  mobile: string;
  image_path: string;
  Email: string;
}

const Profile: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>("");
  const [mobile, setMobile] = useState<string>("");
  const [mobileError, setMobileError] = useState<string>("");
  const [isWebcamModalVisible, setIsWebcamModalVisible] = useState<boolean>(false);
  const [capturedImage, setCapturedImage] = useState<string | undefined>(undefined);
  const [webcamKey, setWebcamKey] = useState<number>(0);

  const webcamRef = useRef<Webcam>(null);

  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: any) => state.auth);
  const { users } = useSelector((state: any) => state.user);

  const userInformation = users?.[0]?.data;
  
  useEffect(() => {
    if (!userInformation) {
      dispatch(getUserById(user?.UserId));
    } else {
      setDisplayName(userInformation.DisplayName || "");
      setMobile(userInformation.mobile || "");
      if (userInformation.image_path) {
        setPreviewImage(userInformation.image_path);
      }
    }
  }, [user, userInformation, dispatch]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFile = e.target.files[0];
      setCapturedImage("")
      setPreviewImage(null);
      if (selectedFile.size > 4 * 1024 * 1024) {
        alert("Please select an image under 4MB.");
        return;
      }
      setProfileImage(selectedFile);
      const imageUrl = URL.createObjectURL(selectedFile);
      setPreviewImage(imageUrl);
    }
  };

  const handleImageCancel = () => {
    setProfileImage(null);
    setPreviewImage(userInformation?.image_path || null);
  };
  // Function to convert base64 string to File object
  const base64ToFile = (base64String: string, filename: string): File => {
    const byteString = atob(base64String.split(',')[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }
    return new File([uint8Array], filename, { type: 'image/jpeg' });
  };

  const validateMobile = (mobile: string) => {
    const regex = /^[0-9]{10,13}$/; // Allow 10 to 13 digits
    if (!regex.test(mobile)) {
      setMobileError("Please enter a valid mobile number with 10 to 13 digits.");
      return false;
    }
    setMobileError("");
    return true;
  };


  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateMobile(mobile)) {
      return; // Stop form submission if validation fails
    }

    // Prepare the updated user data, including the captured image (if any)
    const updatedUserData = {
      DisplayName: displayName,
      mobile: mobile,
      ...(profileImage && { image: profileImage }), // Use selected image from file input
      ...(capturedImage && { image: base64ToFile(capturedImage, 'captured-image.jpg') }) // Convert captured base64 image to File
    };

    const updatedFields: { userId: number; userData: any } = {
      userId: user?.UserId,
      userData: updatedUserData,
    };

    dispatch(updateUser(updatedFields))
      .then(() => {
        alert("Profile updated successfully");
      })
      .catch((error) => {
        alert(error);
      });
  };


  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);

  };

  const showWebcamModal = () => {
    setCapturedImage(undefined);
    setWebcamKey(prev => prev + 1); // Force a fresh render
    setIsWebcamModalVisible(true);
  };

  const stopCamera = () => {
    const video = webcamRef.current?.video;
    if (video && video.srcObject) {
      const stream = video.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      video.srcObject = null;
    }
  };

  const handleWebcamCancel = () => {
    setIsWebcamModalVisible(false);
  };



  const captureImage = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setCapturedImage(imageSrc); // Store captured image as preview
        setProfileImage(null); // Optional: reset the selected image
        setIsWebcamModalVisible(false);
        stopCamera();
      }
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Profile</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.employeeDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Pages</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Profile{" "}
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="border-bottom mb-3 pb-3">
                <h4>Profile</h4>
              </div>
              <form onSubmit={handleUpdateProfile}>
                <div className="border-bottom mb-3">
                  <div className="row">
                    <div className="col-md-12">
                      <div>
                        <h6 className="mb-3">Basic Information</h6>
                        <div className="d-flex align-items-center flex-wrap row-gap-3 bg-light w-100 rounded p-3 mb-4">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl rounded-circle border border-dashed me-2 flex-shrink-0 text-dark frames">
                            {/* Display profile image or fallback icon */}
                            {capturedImage || previewImage ? (
                              <img
                                src={capturedImage || previewImage || "https://cdn-icons-png.flaticon.com/512/149/149071.png"} // Provide a fallback empty string if both are null
                                alt="Profile Preview"
                                className="avatar-img"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  objectFit: "cover",
                                  borderRadius: "50%"
                                }}
                              />
                            ) : (
                              <img src="https://cdn-icons-png.flaticon.com/512/149/149071.png" alt="Fallback Profile" className="avatar-img"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                }}
                              />
                            )}
                          </div>
                          <div className="profile-upload">
                            <div className="mb-2">
                              <h6 className="mb-1">Profile Photo</h6>
                              <p>Image should be below 4 mb</p>
                            </div>
                            <div className="profile-uploader d-flex align-items-center">
                              <div className="drag-upload-btn btn btn-sm btn-primary me-2">
                                {previewImage ? "Reselect" : "Select"}
                                <input
                                  type="file"
                                  className="form-control image-sign"
                                  onChange={handleImageChange}
                                />
                              </div>
                              <div
                                className="drag-upload-btn btn btn-sm btn-primary me-2"
                                onClick={showWebcamModal}
                              >
                                Capture Image
                              </div>
                              {previewImage && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={handleImageCancel}
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">Full Name</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">Email</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            value={userInformation?.Email}
                            className="form-control"
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">Phone</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            value={mobile}
                            onChange={(e) => setMobile(e.target.value)}
                            onInput={(e: any) => {
                              const value = e.target.value.replace(/[^0-9]/g, ""); // Remove non-numeric characters
                              setMobile(value); // Update state with the sanitized number
                              validateMobile(value); // Trigger validation after each change
                            }}
                            className="form-control"
                          />
                          {mobileError && <div className="text-danger mt-2">{mobileError}</div>} {/* Show error */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between">
                  <Button type="primary" onClick={showModal}>
                    Change Password
                  </Button>
                  <div>
                    <button type="submit" className="btn btn-primary">
                      Save
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <ChangePasswordModal visible={isModalVisible} onClose={handleCancel} />

          {/* Webcam Modal */}
          <Modal
            visible={isWebcamModalVisible}
            onCancel={handleWebcamCancel}
            footer={null}
            width={400}
            title="Capture Profile Image"
          >
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Webcam
                key={webcamKey}
                ref={webcamRef} // Attach the ref here
                audio={false}
                screenshotFormat="image/jpeg"
                width="100%"
                videoConstraints={{
                  facingMode: "user",
                }}
              />
            </div>
            <Button
              type="primary"
              onClick={captureImage}
              className="mt-2"
            >
              Capture
            </Button>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default Profile;
